/** First time in edit mode: three short steps (Pencil vs finger, layers, connectors). Persisted when closed. */
import { CornerDownRight, Layers, Pencil, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, cx } from '../components/ui';
import { useT, type MessageKey } from '../i18n';

const STEPS: { icon: LucideIcon; title: MessageKey; body: MessageKey }[] = [
  { icon: Pencil, title: 'annotate.coach1_title', body: 'annotate.coach1_body' },
  { icon: Layers, title: 'annotate.coach2_title', body: 'annotate.coach2_body' },
  { icon: CornerDownRight, title: 'annotate.coach3_title', body: 'annotate.coach3_body' },
];

export function CoachMarks({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [step, setStep] = useState(0);
  const s = STEPS[step]!;
  const last = step === STEPS.length - 1;
  return createPortal(
    <div className="fixed inset-0 z-[56] flex items-end justify-center bg-black/45 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="coach-title">
      <div
        className="flex w-full max-w-md flex-col gap-4 rounded-3xl bg-[var(--surface)] p-6 shadow-[var(--shadow-sheet)]"
        style={{ animation: 'kgr-sheet-in 180ms ease-out', marginBottom: 'var(--safe-bottom)' }}
      >
        <div className="flex items-center gap-3">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
            <s.icon className="size-8" aria-hidden />
          </span>
          <div>
            <p className="text-[14px] font-semibold text-[var(--muted)]">{t('annotate.coach_step', { n: step + 1, total: STEPS.length })}</p>
            <h2 id="coach-title" className="text-[21px] leading-tight font-bold">
              {t(s.title)}
            </h2>
          </div>
        </div>
        <p className="text-[17px] leading-relaxed text-[var(--ink-2)]">{t(s.body)}</p>
        <div className="flex justify-center gap-2" aria-hidden>
          {STEPS.map((_, i) => (
            <span key={i} className={cx('h-2 rounded-full transition-all', i === step ? 'w-6 bg-[var(--accent)]' : 'w-2 bg-[var(--line)]')} />
          ))}
        </div>
        <div className="flex gap-3">
          {!last ? (
            <Button variant="ghost" block onClick={onClose}>
              {t('annotate.coach_skip')}
            </Button>
          ) : null}
          <Button block size="lg" onClick={() => (last ? onClose() : setStep(step + 1))}>
            {last ? t('annotate.coach_start') : t('annotate.coach_next')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
